import React, { useEffect, useState, useRef } from "react";
import questions from "../data/complete_questions";
import RecheckSidePanel from "./widgets/RecheckDialog";
import LoadingOverlay from "./LoadingOverlay";
import { evaluateCode, prepareSubmission } from "../services/openaiService";
import { Button } from "./ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Alert, AlertDescription } from "./ui/alert";
import { AlertCircle } from "lucide-react";
import temperatureTable from "../assets/temperature_table.png";
import { jsPDF } from "jspdf";
import axios from "axios";
import logo from "../assets/avatar4.png";
import file from "../assets/file.svg";
import network from "../assets/network.svg";

const StudentAssessment = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [studentName, setStudentName] = useState("");
  const [teacherEmail, setTeacherEmail] = useState("");
  // answers stores each question’s code keyed by question id
  const [answers, setAnswers] = useState({});
  // feedbacks holds evaluation results for every question after submission
  const [feedbacks, setFeedbacks] = useState([]);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [savefeedback, setSaveFeedback] = useState([]);

  const [isEvaluating, setIsEvaluating] = useState(false);
  const [evaluationError, setEvaluationError] = useState(null);
  const [nameError, setNameError] = useState("");
  const [isResultsSent, setIsResultsSent] = useState(false);
  const selectedQuestion = useRef({});
  const selectedQuestionFeedback = useRef({});
  const selectedQuestionAnswer = useRef({});
  const selectedQuestionIndex = useRef({});


  const errorRef = useRef(null);

  // For controlling the recheck side panel
  const [open, setOpen] = useState(false);
  // Use a ref instead of state to store the selected question id for recheck
  const recheckQuestionIdRef = useRef(null);
  // This can store recheck results per question if needed
  const [recheck, setRecheck] = useState({});

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const email = params.get("email");
    if (email) {
      setTeacherEmail(email);
    }

    const selected = questions
      .filter((q) => params.get(q.id) === "true")
      .map((q) => ({ ...q }));
    setSelectedQuestions(selected);

    const initialAnswers = {};
    selected.forEach((q) => {
      initialAnswers[q.id] = q.initialCode || "";
    });
    setAnswers(initialAnswers);
  }, []);

  // Update answer for a specific question
  const handleCodeChange = (questionId, newCode) => {
    setAnswers((prev) => ({ ...prev, [questionId]: newCode }));
  };

  // Evaluate all questions at once
  const handleSubmit = async () => {
    if (!studentName.trim()) {
      setNameError("Please enter your name before submitting your answer");
      await new Promise((resolve) => setTimeout(resolve, 0));
      if (errorRef.current) {
        errorRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      return;
    }
    setNameError("");
    setIsEvaluating(true);
    setEvaluationError(null);
  
    const newFeedbacks = [];
    // Loop through each question and its parts
    for (const question of selectedQuestions) {
      for (const part of question.parts) {
        try {
          const answer = answers[part.id];
          const submission = prepareSubmission(answer, part.id);
          const feedbackData = await evaluateCode(answer, part.prompt, part.id);
  
          // Parse the feedback: extract marks and explanation.
          const markMatch = feedbackData.match(/[*]*\s*Final\s*Mark\s*[:*]*\s*(\d+)/i);
          const obtained_marks = markMatch ? markMatch[1] : "N/A";
          const feedbackMatch = feedbackData.match(/Explanation to Student:\s*([\s\S]*)/);
          const explanation = feedbackMatch ? feedbackMatch[1].trim() : "";
  
          setSaveFeedback((prevData) => {
            const newEntry = {
              id: part.id,
              question_marks: part.marks,
              obtained_marks,
              question: part.text,
              answer,
              feedback: explanation,
            };
            const updatedData = [...prevData, newEntry];
            console.log("Updated Data:", updatedData);
            return updatedData;
          });
  
          newFeedbacks.push({
            id: part.id,
            question_marks: part.marks,
            obtained_marks,
            question: part.text,
            answer,
            feedback: explanation,
          });
        } catch (error) {
          console.error("Error evaluating part", part.id, error);
          newFeedbacks.push({
            id: part.id,
            question_marks: part.marks,
            obtained_marks: "Error",
            question: part.text,
            answer: answers[part.id],
            feedback: "Error evaluating this question.",
          });
        }
      }
    }
    setFeedbacks(newFeedbacks);
    setIsEvaluating(false);
    setIsSubmitted(true);
  
    // Data tracking for each part
    newFeedbacks.forEach(async (entry) => {
      const requestData = {
        teacherEmail: teacherEmail,
        studentName: studentName,
        feedbackHistory: JSON.stringify(entry),
      };
      try {
        const response = await axios.post(
          "https://staging.teepee.ai//teepee_web_server_test/examai_submit_track",
          {
            method: "POST",
            body: requestData,
          }
        );
        if (response) {
          console.log("Data saved for part", entry.id);
        } else {
          console.log("Data not saved for part", entry.id);
        }
      } catch (error) {
        console.error("Error saving tracking data for part", entry.id, error);
      }
    });
  };
  

  // Function to share results with teacher
  const sendResultsToTeacher = async () => {
    try {
      const results = {
        teacherEmail: teacherEmail,
        studentName: studentName,
        questions: selectedQuestions.map((q, index) => {
          const feedbackEntry = feedbacks.find((f) => f.id === q.id) || {};
          return {
            questionNumber: index + 1,
            questionText: q.text,
            studentCode: answers[q.id],
            marks: feedbackEntry.obtained_marks || 0,
            totalPossibleMarks: q.marks,
            feedback: feedbackEntry.feedback || "",
          };
        }),
        totalMarks: selectedQuestions.reduce((total, q) => {
          const feedbackEntry = feedbacks.find((f) => f.id === q.id);
          return (
            total +
            (feedbackEntry && !isNaN(Number(feedbackEntry.obtained_marks))
              ? Number(feedbackEntry.obtained_marks)
              : 0)
          );
        }, 0),
        totalPossibleMarks: selectedQuestions.reduce((total, q) => total + q.marks, 0),
      };

      const response = await fetch("https://staging.teepee.ai/teepee_web_server_test/mockp1_email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(results),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to send results");
      }

      setIsResultsSent(true);
    } catch (error) {
      console.error("Error sending results:", error);
      alert("Failed to send results to teacher. Please try again.");
    }
  };

  // Download PDF uses the feedbacks array; remains largely unchanged.
  const downloadPDF = () => {
    if (!Array.isArray(feedbacks) || feedbacks.length === 0) {
      console.error("Error: No feedback available.");
      return;
    }
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;
    const contentWidth = pageWidth - margin * 2;
    let yOffset = margin;

    const styles = {
      title: { fontSize: 22, style: "bold", spacing: 20 },
      sectionHeader: { fontSize: 14, style: "bold", spacing: 8 },
      subHeader: { fontSize: 12, style: "bold", spacing: 6 },
      normal: { fontSize: 10, style: "normal", spacing: 6 },
      codeEditor: { fontSize: 9, style: "normal", spacing: 8, background: "#f4f4f4", fontFamily: "Courier" },
    };

    const addText = (text, options = {}) => {
      const {
        fontSize = styles.normal.fontSize,
        style = "normal",
        indent = 0,
        spacing = styles.normal.spacing,
        maxWidth = contentWidth - indent,
        fontFamily = "helvetica",
        align = "left",
      } = options;

      if (yOffset + fontSize * 0.3527 * 2 > pageHeight - margin) {
        doc.addPage();
        yOffset = margin;
      }
      doc.setFontSize(fontSize);
      doc.setFont(fontFamily, style);

      const lines = doc.splitTextToSize(text, maxWidth);
      const textHeight = lines.length * fontSize * 0.3527;

      if (align === "center") {
        lines.forEach((line, i) => {
          const textWidth = doc.getTextWidth(line);
          const xPos = (pageWidth - textWidth) / 2;
          doc.text(line, xPos, yOffset + fontSize * 0.3527 + i * fontSize * 0.3527);
        });
      } else {
        doc.text(lines, margin + indent, yOffset + fontSize * 0.3527);
      }
      yOffset += textHeight + spacing;
      return yOffset;
    };

    const addHeaderBox = (text, options = {}) => {
      const {
        fontSize = styles.sectionHeader.fontSize,
        style = styles.sectionHeader.style,
        padding = 4,
        bgColor = [200, 200, 255],
        textColor = [0, 0, 0],
        spacing = styles.sectionHeader.spacing,
      } = options;
      doc.setFontSize(fontSize);
      doc.setFont("helvetica", style);
      const lines = doc.splitTextToSize(text, contentWidth);
      const textHeight = lines.length * fontSize * 0.3527;
      const boxHeight = textHeight + padding * 2;

      doc.setFillColor(...bgColor);
      doc.rect(margin, yOffset, contentWidth, boxHeight, "F");

      doc.setTextColor(...textColor);
      lines.forEach((line, i) => {
        const textWidth = doc.getTextWidth(line);
        const xPos = margin + (contentWidth - textWidth) / 2;
        doc.text(line, xPos, yOffset + padding + fontSize * 0.3527 + i * fontSize * 0.3527);
      });
      yOffset += boxHeight + spacing;
      doc.setTextColor(0, 0, 0);
    };

    const addCodeEditor = (code, options = {}) => {
      const {
        fontSize = styles.codeEditor.fontSize,
        fontFamily = styles.codeEditor.fontFamily,
        lineHeight = fontSize * 0.3527,
        padding = 8,
      } = options;

      doc.setFontSize(fontSize);
      doc.setFont(fontFamily, styles.codeEditor.style);

      const lines = doc.splitTextToSize(code, contentWidth - padding * 2);
      let currentLine = 0;

      while (currentLine < lines.length) {
        const remainingSpace = pageHeight - margin - yOffset;
        const linesPerPage = Math.floor((remainingSpace - padding * 2) / lineHeight);
        if (linesPerPage < 1) {
          doc.addPage();
          yOffset = margin;
          continue;
        }
        const currentPageLines = lines.slice(currentLine, currentLine + linesPerPage);
        const boxHeight = currentPageLines.length * lineHeight + padding * 2;

        doc.setFillColor(244, 244, 244);
        doc.rect(margin, yOffset, contentWidth, boxHeight, "F");

        doc.setDrawColor(200, 200, 200);
        doc.rect(margin, yOffset, contentWidth, boxHeight);

        doc.setTextColor(0, 0, 0);
        currentPageLines.forEach((line, i) => {
          doc.text(line, margin + padding, yOffset + padding + i * lineHeight + fontSize * 0.3527);
        });

        yOffset += boxHeight + styles.codeEditor.spacing;
        currentLine += linesPerPage;
        if (currentLine < lines.length) {
          doc.addPage();
          yOffset = margin;
        }
      }
      return yOffset;
    };

    addHeaderBox("Feedback Report", {
      fontSize: styles.title.fontSize,
      style: styles.title.style,
      bgColor: [100, 149, 237],
      textColor: [255, 255, 255],
      spacing: 20,
    });

    feedbacks.forEach((feedback, index) => {
      if (index > 0) {
        doc.setDrawColor(180, 180, 180);
        doc.line(margin, yOffset, pageWidth - margin, yOffset);
        yOffset += 10;
      }

      const questionHeader = `Question ${index + 1}      Obtained Marks: ${feedback.obtained_marks} / ${feedback.question_marks}`;
      addHeaderBox(questionHeader, {
        fontSize: styles.subHeader.fontSize,
        style: styles.subHeader.style,
        bgColor: [220, 220, 220],
        textColor: [0, 0, 0],
        spacing: 10,
      });

      addText(feedback.question, { ...styles.normal, indent: 5, spacing: 12 });
      addText("Answer:", { ...styles.sectionHeader, indent: 0, spacing: 4 });
      addCodeEditor(feedback.answer, {
        fontSize: styles.codeEditor.fontSize,
        fontFamily: styles.codeEditor.fontFamily,
      });
      addText("Feedback:", { ...styles.sectionHeader, indent: 0, spacing: 4 });
      addText(feedback.feedback, { ...styles.normal, indent: 5, spacing: 15 });
    });

    const pageCount = doc.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(150);
      doc.text(`Page ${i} of ${pageCount}`, pageWidth - margin - 30, pageHeight - 10);
    }

    doc.save("feedback_report_enhanced.pdf");
  };

  useEffect(() => {
    console.log("Current recheckQuestionId (state):", recheckQuestionIdRef.current);
  }, [open]);

  // Update handleRecheck to use the ref for immediate update
  const handleRecheck = (questionId) => {
    console.log("handleRecheck called with QID:", questionId);
    if (!questionId) {
      console.error("handleRecheck called with an undefined questionId");
      return;
    }
    recheckQuestionIdRef.current = questionId;

    const nestedParts =  [];

      for (let i = 0; i < selectedQuestions.length; i++){
        console.log("sa",selectedQuestions[i]);
        console.log("sa",selectedQuestions[i].id);

        nestedParts.push(
        selectedQuestions[i].parts
        )

      }



// Flatten the nested array and then find the matching question
const foundQuestion = nestedParts.flat().find(
  q => q.id.toString().trim() === recheckQuestionIdRef.current.toString().trim()
);
// Iterate over the parent arrays and their nested parts
let parentIndex = -1;  // To store the parent array index
let foundQuestionIndex = -1; // To store the nested part index

for (let i = 0; i < selectedQuestions.length; i++) {
  // Iterate through the parts of each selected question
  for (let j = 0; j < selectedQuestions[i].parts.length; j++) {
    const part = selectedQuestions[i].parts[j];

    // Check if the question id matches
    if (part.id.toString().trim() === recheckQuestionIdRef.current.toString().trim()) {
      parentIndex = i;  // Store the parent index
      foundQuestionIndex = j;  // Store the nested part index
      break;  // Exit loop once a match is found
    }
  }

  // Break outer loop if a match is found
  if (parentIndex !== -1) {
    break;
  }
}
let result = -1 ;
// If a match was found, calculate result
if (parentIndex !== -1 && foundQuestionIndex !== -1) {
  result = (parentIndex + 1) ;
  console.log("Parent Index:", parentIndex);
  console.log("Nested Question Index:", foundQuestionIndex);
  console.log("Result:", result);
} else {
  console.log("Question not found");
}


selectedQuestion.current = foundQuestion || null;
selectedQuestionIndex.current = result;
    selectedQuestionFeedback.current = feedbacks.find(f => f.id.toString() === recheckQuestionIdRef.current.toString());
    selectedQuestionAnswer.current=answers[recheckQuestionIdRef.current] || "";
    setOpen(true);

  };

  // A simple handler to open Teepee.ai site
  const handleClick = () => {
    const url = "https://app.teepee.ai/";
    window.open(url, "_blank");
  };

  const groupedSums = feedbacks.reduce((acc, feedback, index) => {
    const groupIndex = Math.floor(index / 3);
    if (!acc[groupIndex]) {
      acc[groupIndex] = 0;
    }
    acc[groupIndex] += Number(feedback.obtained_marks); // Convert to number
    return acc;
  }, []);

  const obtained_marks = groupedSums.reduce((acc, curr) => acc + curr, 0);
  const total_marks = questions.reduce((acc, obj) => acc + obj.marks, 0);

  console.log("Obtained",obtained_marks," Total",total_marks)

  const allAnswersFilled = selectedQuestions.every(question =>
    question.parts.every(part => answers[part.id] && answers[part.id].trim() !== "")
  );
console.log('Sums',groupedSums)
  return (
    <div className="container mx-auto ">
      <nav className="bg-white py-4 px-6 shadow-sm sticky top-0 z-10">
        <div className="max-w-8xl mx-auto flex items-center justify-between ml-10">
          <div className="flex items-center">
            <a href="https://teepee.ai" target="_blank">
              <img 
                src={logo}
                alt="Teepee.ai Logo"
                width={200}
                height={40}
                className="cursor-pointer"
              />
            </a>
          </div>
          <div className="flex items-center gap-10 mt-2">
            <a href="https://teepee.ai/" target="_blank" className="text-gray-700 hover:text-gray-900 text-[16px] font-poppins">
              Home
            </a>
            <div className="flex items-center gap-2">
              <a href="https://app.teepee.ai/" target="_blank" className="text-[#6750A4] hover:text-purple-700 text-[16px] font-poppins">
                Unlimited Exam Practice, Instant Feedback
              </a>
              <span className="text-purple-600">→</span>
            </div>
            <button onClick={handleClick} className="bg-[#6750A4] text-white px-4 py-2 rounded-[48px] text-[16px] font-poppins font-medium hover:bg-purple-700">
              Try for free
            </button>
          </div>
        </div>
      </nav>

      {/* Render RecheckSidePanel if a question is selected */}
      {open && recheckQuestionIdRef.current && (
        <RecheckSidePanel
          open={open}
          selectedQuestion={selectedQuestion.current}
          totalQuestions={selectedQuestions.length}
          selectedQuizQuestion={selectedQuestion.current.text}

          finalTotalMark={selectedQuestionFeedback.current.obtained_marks}
          feedbackAI={selectedQuestionFeedback.current.feedback}
          studentAsnwer={selectedQuestionAnswer.current}
          closePanel={() => {
            setOpen(false);
            recheckQuestionIdRef.current = null;
          }}
          setRecheck={setRecheck}
          recheck={recheck[recheckQuestionIdRef.current] || {}}
          setSaveFeedback={setSaveFeedback}
          selectedQuestionIndex = {selectedQuestionIndex.current}

        />
      )}

      <Card>

        <CardHeader>
        <div className="flex" style= {{justifyContent:"space-between"}}>
        <div className="max-w-sm my-6" style= {{}}>
              {/* Label */}

           <div
            style={{
              padding: "26px",
              border: "1px solid #000",
              borderRadius: "15px",
            }}
            className="flex justify-between items-center"
          >
            {/* Student Name Input */}
            <div className=" pr-2">
              <label
                htmlFor="studentName"
                className="block text-base font-bold text-gray-700 mb-1"
              >
                Student Name
              </label>
              <div className="border-b border-gray-950">
                <input
                  id="studentName"
                  type="text"
                  value={studentName}
                  onChange={(e) => {
                    setStudentName(e.target.value);
                    setNameError("");
                  }}
                  placeholder=""
                  className="
                    w-full
                    bg-transparent
                    border-0
                    px-0
                    py-1
                    text-gray-800
                    focus:outline-none
                    focus:ring-0
                  "
                />
              </div>
            </div>

          {/* Total Marks Display (only if available) */}
          {isSubmitted && (
            <div className="w-1/2 pl-2 border-l border-gray-950">
            <label
              htmlFor="totalMarks"
              className="block text-base font-bold text-gray-700 mb-1 text-center"
            >
              Total Marks
            </label>
            <div className="flex justify-center">
              <span
                id="totalMarks"
                className="bg-transparent border-0 px-0 py-1 text-gray-800"
              >
                {obtained_marks} / {total_marks}
              </span>
            </div>
          </div>
          
          )}
        </div>

          </div>
        {isSubmitted && (
          <div className="mt-8 flex flex-col items-end space-y-4">
              <Button
                onClick={async () => {
                  try {
                    setIsLoading(true);
                    
                    const requestData = {
                      body: {
                        teacherEmail: teacherEmail,
                        studentName: studentName,
                        feedbackHistory: savefeedback,
                      },
                    };
                    
                    // Format different sending response to owner
                    const response = await fetch("https://staging.teepee.ai/teepee_web_server_test/mockp1_email", {
                      method: "POST",
                      headers: {
                        "Content-Type": "application/json",
                      },
                      body: JSON.stringify(requestData),
                    });
                    
                    if (!response.ok) {
                      const errorData = await response.json();
                      throw new Error(errorData.error || "Failed to send results");
                    }
                    
                    setIsResultsSent(true);
                    setIsLoading(false);
                  } catch (error) {
                    console.error("Error sending results:", error);
                    setIsLoading(false);
                    alert("Failed to send results to teacher. Please try again.");
                  }
                }}
                disabled={isResultsSent || isLoading}
                className="bg-[#6750A4] text-white border border-[#6750A4] hover:bg-[#56378a] hover:border-[#56378a] active:bg-[#462e70] active:border-[#462e70] transition-all duration-200 px-4 py-2 rounded flex items-center"
                >
                <img
                  src={network}
                  alt="Send Icon"
                  className="inline-block w-4 h-4 mr-2"
                  style={{ filter: "brightness(0) invert(1)" }}
                  />
                {isLoading ? "Sending..." : isResultsSent ? "Results Sent!" : "Share Assessment with Teacher"}
              </Button>

              <Button
                onClick={downloadPDF}
                className="bg-white text-[#6750A4] border border-gray-400 hover:bg-[#474256] hover:text-white active:bg-blue-700 active:border-blue-800 transition-all duration-200 px-4 py-2 rounded flex items-center"
                >
                <img
                  src={file}
                  alt="PDF Icon"
                  className="w-4 h-4 mr-2"
                  style={{ filter: "invert(39%) sepia(63%) saturate(300%) hue-rotate(230deg) brightness(90%) contrast(90%)" }}
                  />
                Download PDF
              </Button>
            </div>
          )}
          </div>


          
            {nameError && (
                        <Alert variant="destructive" className="bg-red-50 border-red-200" ref={errorRef}>
                          <AlertCircle className="h-4 w-4" />
                          <AlertDescription className="flex items-center">
                            <span className="ml-2">{nameError}</span>
                          </AlertDescription>
                        </Alert>
             )}
          
          <CardTitle>
          <div className="flex flex-col items-center justify-center text-center">
            <h1
              className="text-[19px] leading-[40px] font-semibold mb-2"
              style={{ fontFamily: "Roboto", marginTop: "24px" ,letterSpacing: "0.15px"}}
            >
              Answer ALL questions. Write your answers in the spaces provided.
            </h1>
            <p
              className="text-[19px] leading-[40px] font-semibold"
              style={{ fontFamily: "Roboto" ,letterSpacing: "0.15px"}}
            >
Please ensure you have answered all questions and entered your name in the required field. You will only be able to submit once all sections are complete.</p>
          </div>

          
           </CardTitle>
          </CardHeader>
          <CardContent>
          <div className="student-assessment relative">
            {isEvaluating && <LoadingOverlay />}


            {selectedQuestions.length === 0 && <p>Loading questions or no questions selected.</p>}




            {/* Render all questions with their text, editor, and feedback if submitted */}



            {
              selectedQuestions.map((question, qIndex) => (
                <div key={question.id} className="mb-8 p-4 rounded-md">
                  {/* Display question summary as header */}
                  <h2 className="text-xl font-semibold mb-4">
                  Question: {qIndex + 1} {question.summary} {/* e.g., "Q1 summary" or "Q2 summary" */}
                  </h2>

                  {/* Map through all parts for this question */}
                  {question.parts.map((part) => (
                <div key={part.id} className="mb-8 p-4 rounded-md">
                  <div className="mb-4">
                    {part.text.split("\n").map((line, idx, arr) => {
                      const trimmedLine = line.trim();

                      // Bullet points (lines starting with "-")
                      if (trimmedLine.startsWith("-")) {
                        return (
                          <div key={idx}>
                            <span className="m-0" />
                            {trimmedLine.substring(1)}
                          </div>
                        );
                      } 

                  // Regular text lines
                  else if (trimmedLine && !trimmedLine.startsWith("[")) {
                    const textStyle = {
                      fontFamily: "Roboto",
                      fontSize: "16px",
                      lineHeight: "28px",
                      letterSpacing: "0.15px",
                    };

                    // First line will be bold
                    const firstLineStyle = {
                      ...textStyle,
                      fontWeight: "bold",
                    };

                    // Subsequent lines will have indentation (tab space)
                    const subsequentLineStyle = {
                      ...textStyle,
                      paddingLeft: "1em",
                    };

                    // Apply styles depending on whether it's the first line or not
                    const appliedStyle = idx === 0 ? firstLineStyle : subsequentLineStyle;

                    // If it's the last line, show marks as well
                    if (idx === arr.length - 1) {
                      // Define the exact string to match
                      const targetString = '(i) Explain what technical issue has occurred in this addition and why.';
                      
                      // If the trimmedLine matches the target string, add non-breaking spaces
                      const finalLine = trimmedLine === targetString
                        ? `&nbsp;${trimmedLine}` // Using 4 non-breaking spaces instead of a tab
                        : trimmedLine;
                      
                      return (
                        <div key={idx} className="mb-4 flex justify-between items-center">
                          <p className="m-0" style={appliedStyle} dangerouslySetInnerHTML={{ __html: finalLine }} />
                          <span style={appliedStyle}>({part.marks})</span>
                        </div>
                      );
                    }

                    return (
                      <p key={idx} className="mb-4" style={appliedStyle}>
                        {trimmedLine}
                      </p>
                    );
                  } 

                  // Display image block
                  else if (trimmedLine.includes("[Display image:")) {
                    const imagePath = trimmedLine.match(/\[(Display image: .*?)\]/)[1];
                    let imageSource = temperatureTable;
                    if (imagePath.includes("temperature_table.png")) {
                      imageSource = temperatureTable;
                    }
                    return (
                      <div key={idx} className="my-4">
                        <img
                          src={imageSource}
                          alt="Question reference"
                          className="max-w-[400px] h-auto rounded-md border border-gray-200"
                        />
                      </div>
                    );
                  }

                  return <div key={idx} className="h-4" />;
                })}
              </div>

              <textarea
                value={answers[part.id] || ""}
                onChange={(e) => handleCodeChange(part.id, e.target.value)}
                readOnly={isSubmitted}
                rows={10}
                placeholder="Enter your answer here..."
                style={{
                  width: "100%",
                  padding: "12px",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  fontSize: "16px",
                  color: "#333",
                  backgroundColor: "rgb(255 255 255)",
                  transition: "border-color 0.3s ease, box-shadow 0.3s ease",
                  outline: "none",
                  boxSizing: "border-box",
                  fontFamily: "Roboto",
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#66afe9";
                  e.target.style.boxShadow = "0 0 8px rgba(102, 175, 233, 0.6)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#ccc";
                  e.target.style.boxShadow = "none";
              }}
              />

              {isSubmitted && feedbacks.length > 0 && (
                <div>
                  {feedbacks.find((f) => f.id === part.id)?.obtained_marks < part.marks * 0.5 ? (
                    <div
                      className="mt-4 p-4 border rounded"
                      style={{ backgroundColor: "rgba(185,28,28,0.1)" }}
                    >
                      <div className="text-red-500">
                        <h3 className="text-lg font-semibold">Feedback:</h3>
                        <p>
                          <strong>Obtained Marks: </strong>
                          {feedbacks.find((f) => f.id === part.id)?.obtained_marks} / {part.marks}
                        </p>
                        <p>{feedbacks.find((f) => f.id === part.id)?.feedback}</p>
                      </div>
                    </div>
                  ) : (
                    <div
                      className="mt-4 p-4 border rounded"
                      style={{ backgroundColor: "rgba(21,128,61,0.1)" }}
                    >
                      <div className="text-green-500">
                        <h3 className="text-lg font-semibold">Feedback:</h3>
                        <p>
                          <strong>Obtained Marks: </strong>
                          {feedbacks.find((f) => f.id === part.id)?.obtained_marks} / {part.marks}
                        </p>
                        <p>{feedbacks.find((f) => f.id === part.id)?.feedback}</p>
                      </div>
                    </div>
                  )}
                  <div className="mt-2 text-[0.69rem] text-gray-700" style={{ fontFamily: "Roboto" }}>
                    ⚠️ Not happy with this result?{" "}
                    <span
                      onClick={!recheck[part.id] ? () => handleRecheck(part.id) : undefined}
                      className={`text-blue-500 underline cursor-pointer ${recheck[part.id] ? "opacity-50 cursor-not-allowed" : ""}`}
                    >
                      [Request AI Recheck]
                    </span>
                  </div>
                </div>
              )}
              </div>
          ))
        }


                  {groupedSums && groupedSums.length > 0 ? (
                    <h2
                    className="mb-4 text-[16px] leading-[8px] tracking-[0.15px] text-right font-semibold"
                    style={{ fontFamily: "Roboto" }}
                  >
                    (Total for Question {qIndex + 1} = {groupedSums[qIndex]} / {question.marks} marks)
                  </h2>
                  ) : (
                    <h2
                      className="mb-4 text-[16px] leading-[8px] tracking-[0.15px] text-right font-semibold"
                      style={{ fontFamily: "Roboto" }}
                    >
                      (Total for Question {qIndex + 1} = {question.marks} marks)
                    </h2>
                  )}

                </div>
              ))
            }














{!isSubmitted && (
  <div
    className="mt-4"
    style={{ marginLeft: "1.8rem", marginTop: "-3.3rem" }}
  >
    <Button
      onClick={handleSubmit}
      disabled={!allAnswersFilled || isEvaluating}
      style={{
        backgroundColor: "#6750A4",
        color: "white" // ensures the text is visible on the dark background
      }}
    >
      {isEvaluating ? "Evaluating..." : "Submit for Marking"}
    </Button>
    <div
      style={{
        marginTop: "0.5rem",
        fontSize: "0.7rem",
        color: "#555"
      }}
    >
     Note: Please ensure you have answered all questions and entered your name above before submitting.
    </div>
  </div>
)}



          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default StudentAssessment;
